import React, { ReactNode } from "react"

import { Action } from "shared/types/Action"
import { ApiResponse } from "shared/types/ApiResponse"
import { DropHandler } from "shared/types/DragAndDrop"
import { ProductPosition } from "shared/types/ProductPosition"
import { Project } from "shared/types/Project"
import { Ref } from "shared/types/Ref"
import { Room } from "shared/types/Room"
import { WithClassName } from "shared/types/WithClassName"
import type { ProductPositionAddon } from "shared/types/__generated__/graphql"
import { DragSource } from "shared/types/drag-and-drop/DragSource"
import { DropTarget } from "shared/types/drag-and-drop/DropTarget"
import type { RowRendererProps, TableAPI } from "shared/ui/table/types"

import { SelectElementOptions } from "features/project-management/model/store/projectStoreActionHandlers"
import { ProjectModel } from "features/project-management/types/ProjectModel"
import { FloorTabRendererProps } from "pages/project-page/ui/project-page-tab-bar/ProjectPageFloorTabConnector"

import { ProjectPageView } from "./ProjectPageView"
import { useProjectPageViewModel } from "./hooks/useProjectPageViewModel"

export interface ProjectPageModel {
  projectModel?: ProjectModel | null

  onActions?: () => void
  onUpdate?: (project: Partial<Project>) => void | Promise<ApiResponse<Project>>
  onManageProjectTeam?: () => void

  hideChat?: boolean

  onSelect?: (id: string[], options?: SelectElementOptions) => void
  onPositionChange?: DropHandler
  onFloorSort?: (floorID: string, oldIndex: number, newIndex: number) => void
  onDirectDropIntoFloor?: (
    dragSource: DragSource,
    dropTarget: DropTarget
  ) => void

  noDataHint?: React.ReactNode

  activeTabID?: string | null
  selectedElementIDs?: string[]
  isItemCollapsed?: (item: { id: string; __typename?: string }) => boolean
  onItemCollapse?: (id: string) => void
  onCollapseAll?: () => void
  onExpandAll?: () => void
  onTabSelect?: (
    floorID: string | null,
    options?: { keepSelection?: boolean }
  ) => void
  pendingProjectChildren?: string[]

  isSortingDisabled?: boolean

  sidebar?: ReactNode

  actionsButtonLabel?: string
  additionalActions?: Action[]

  tableAPIRef?: Ref<TableAPI> | null
  RowRenderer?: React.ComponentType<
    RowRendererProps<ProductPosition | ProductPositionAddon | Room>
  >
  FloorTabRenderer?: React.ComponentType<FloorTabRendererProps>

  chatWidgets?: React.ReactNode
  chatButton?: React.ReactNode
}

export interface ProjectPageProps extends ProjectPageModel, WithClassName {
  hideQuantity?: boolean
  hideActions?: boolean
  hideTabBar?: boolean
  showSelectionCheckbox?: boolean
}

function ProjectPageComponent(props: ProjectPageProps) {
  return <ProjectPageView {...useProjectPageViewModel(props)} />
}

export const ProjectPage = React.memo(ProjectPageComponent)
